import * as React from 'react';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';
import defaultImage from '@/assets/images/placeholders/show-course.jpg';

const CourseCard = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-course-card', 'flex cursor-pointer flex-wrap items-end gap-8 rounded-xl bg-white p-6 transition-shadow duration-200 ease-in-out', 'shadow-dropshadow', 'hover:shadow-[0_6px_10px_rgba(0,0,0,0.15)]', 'lg:gap-[60px]', className)}
    tabIndex={0}
    {...props}
  />
));
CourseCard.displayName = 'CourseCard';

const CourseCardImgWrapper = React.forwardRef(({ className, ...props }, ref) => (
  <figure
    ref={ref}
    className={cn('relative', '[&>.c-badge]:absolute', '[&>.c-badge]:left-2', '[&>.c-badge]:top-2', '[&>.c-badge]:z-10', '[&>.c-badge]:max-w-[80px]', className)}
    {...props}
  />
));
CourseCardImgWrapper.displayName = 'CourseCardImgWrapper';

const courseCardImgVariants = cva('rounded-[12px] object-cover', {
  variants: {
    size: {
      default: 'h-[110px] w-[110px]',
      md: 'h-[136px] w-[136px] lg:w-[272px]'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const CourseCardImg = React.forwardRef(({ src, alt = '', size, className, ...props }, ref) => {
  const [imgSrc, setImgSrc] = React.useState(src || defaultImage);

  const handleError = () => {
    if (imgSrc !== defaultImage) {
      setImgSrc(defaultImage);
    }
  };

  React.useEffect(() => {
    setImgSrc(src || defaultImage);
  }, [src]);

  return (
    <img
      ref={ref}
      src={imgSrc}
      onError={handleError}
      alt={alt}
      className={cn(courseCardImgVariants({ size, className }))}
      {...props}
    />
  );
});
CourseCardImg.displayName = 'CourseCardImg';

const CourseCardHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-3', className)}
    {...props}
  />
));
CourseCardHeader.displayName = 'CourseCardHeader';

const CourseCardInnerHeader = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex w-0 flex-grow flex-col gap-1', className)}
    {...props}
  />
));
CourseCardInnerHeader.displayName = 'CourseCardInnerHeader';

const CourseCardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn('mb-0 truncate text-base font-bold leading-none text-primary-dark', className)}
    {...props}
  />
));
CourseCardTitle.displayName = 'CourseCardTitle';

const CourseCardSubtitle = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mb-1 text-sm font-light leading-none text-neutral-light', '[&>strong]:text-neutral', className)}
    {...props}
  />
));
CourseCardSubtitle.displayName = 'CourseCardSubtitle';

const CourseCardText = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mb-1 text-sm font-light leading-none text-neutral-light', '[&>strong]:text-neutral', className)}
    {...props}
  />
));
CourseCardText.displayName = 'CourseCardText';

const CourseCardNotifications = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-shrink-0 items-center gap-3', className)}
    {...props}
  />
));
CourseCardNotifications.displayName = 'CourseCardNotifications';

const CourseCardAutor = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('mt-2 text-sm text-neutral', className)}
    {...props}
  />
));
CourseCardAutor.displayName = 'CourseCardAutor';

const CourseCardDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('my-[1vw] mb-[1.5vw] text-sm text-neutral-dark', className)}
    {...props}
  />
));
CourseCardDescription.displayName = 'CourseCardDescription';

const courseCardBodyVariants = cva('flex flex-grow gap-6', {
  variants: {
    alignment: {
      default: 'items-center',
      home: 'items-stretch'
    }
  },
  defaultVariants: {
    alignment: 'default'
  }
});

const CourseCardBody = React.forwardRef(({ className, alignment, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(courseCardBodyVariants({ alignment }), '[&>.c-medal]:flex-shrink-0', '[&>.c-medal]:self-center', '[&>.c-medal]:flex-shrink-0', '[&>.c-medal]:self-center', className)}
    {...props}
  />
));
CourseCardBody.displayName = 'CourseCardBody';

const CourseCardInnerBody = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('w-[120px] flex-shrink flex-grow', className)}
    {...props}
  />
));
CourseCardInnerBody.displayName = 'CourseCardInnerBody';

const CourseCardContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex w-[120px] flex-shrink flex-grow flex-col justify-between py-1.5', className)}
    {...props}
  />
));
CourseCardContent.displayName = 'CourseCardContent';

const CourseCardTag = React.forwardRef(({ className, children, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('inline-flex flex-shrink-0 items-center justify-center gap-2 self-start rounded-md bg-success px-3 py-1.5 text-xs font-bold leading-none text-white', className)}
    {...props}
  >
    {children}
  </span>
));
CourseCardTag.displayName = 'CourseCardTag';

const CourseCardFooter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('w-full flex-shrink-0 lg:w-[152px]', className)}
    {...props}
  />
));
CourseCardFooter.displayName = 'CourseCardFooter';

const CourseCardLink = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('[&>a]:flex [&>a]:items-center [&>a]:gap-1 [&>a]:leading-none', '[&>a]:mb-0', className)}
    {...props}
  />
));
CourseCardLink.displayName = 'CourseCardLink';

const CourseCardLinkText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('line-clamp-1 text-sm font-bold text-neutral-dark underline', className)}
    {...props}
  />
));
CourseCardLinkText.displayName = 'CourseCardLinkText';

const courseCardWrapperVariants = cva('flex flex-col gap-3 overflow-y-auto p-3 -m-3', {
  variants: {
    view: {
      default: '',
      list: 'max-h-[550px] lg:max-h-[344px]'
    }
  },
  defaultVariants: {
    view: 'default'
  }
});

const CourseCardWrapper = React.forwardRef(({ className, view, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(courseCardWrapperVariants({ view, className }))}
    {...props}
  />
));
CourseCardWrapper.displayName = 'CourseCardWrapper';

export {
  CourseCard,
  CourseCardImgWrapper,
  CourseCardImg,
  CourseCardHeader,
  CourseCardInnerHeader,
  CourseCardTitle,
  CourseCardText,
  CourseCardSubtitle,
  CourseCardNotifications,
  CourseCardAutor,
  CourseCardDescription,
  CourseCardBody,
  CourseCardInnerBody,
  CourseCardContent,
  CourseCardTag,
  CourseCardFooter,
  CourseCardLink,
  CourseCardLinkText,
  CourseCardWrapper
};
